import { useMemo, useState } from 'react';
import { useI18n } from '@/i18n';
import { useTenant } from '@/contexts/TenantContext';
import {
  Badge,
  Button,
  Card,
  Input,
  SearchInput,
  Spinner,
} from '@/components/ui';
import { MarketplaceCard } from '@/components/ui/marketplace-card';
import { PageHeader } from '@vanguard-atlas/shared';
import { AppModal } from '@/components/ui/app-modal';
import {
  useCACAcceptOffer,
  useCACCatalog,
  useCACContractAcceptance,
  useCACImportPackage,
  useCACPublicationRequests,
} from '@/features/cac';
import type { CACCatalogItem, CACPublicationRequest } from '@/features/cac';
import { PackageSearch, ShieldAlert } from 'lucide-react';

function publicErrorMessage(error: unknown): string {
  const defaultMessage = 'Operation failed. Please retry.';
  if (!error || typeof error !== 'object') {
    return defaultMessage;
  }

  const maybeAxios = error as {
    response?: {
      data?: {
        detail?: { code?: string; message?: string };
      };
    };
  };

  const detail = maybeAxios.response?.data?.detail;
  if (!detail?.code) {
    return defaultMessage;
  }

  switch (detail.code) {
    case 'CAC_PRIVATE_OFFER_NOT_TARGET':
      return 'Offer is not available for the selected tenant.';
    case 'CAC_BILLING_CONFIRMATION_FAILED':
      return 'Billing confirmation is temporarily unavailable.';
    case 'CAC_ENTITLEMENT_DENIED':
    case 'CAC_ENTITLEMENT_REQUIRED':
      return 'Active entitlement is required before import.';
    case 'CAC_DR_IMPORT_FAILED':
      return 'Import dispatch failed. Please retry.';
    case 'CAC_CONTRACT_NOT_ACCEPTED':
      return 'You must accept the license terms before importing.';
    default:
      return detail.message || defaultMessage;
  }
}

function publicationStatusBadge(request: CACPublicationRequest) {
  if (request.status === 'published') {
    return <Badge variant="default">published</Badge>;
  }
  if (request.status === 'failed' || request.status === 'rejected') {
    return <Badge variant="destructive">{request.status}</Badge>;
  }
  return <Badge variant="secondary">{request.status}</Badge>;
}

export function CACMarketplacePage() {
  const { t: _t } = useI18n();
  void _t;
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  const [search, setSearch] = useState('');
  const [regionCode, setRegionCode] = useState('eu-w1');
  const [pendingImport, setPendingImport] = useState<CACCatalogItem | null>(null);
  const [contractAccepted, setContractAccepted] = useState(false);

  const catalogQuery = useCACCatalog(tenantId);
  const publicationRequestsQuery = useCACPublicationRequests(tenantId);
  const acceptOffer = useCACAcceptOffer(tenantId);
  const importPackage = useCACImportPackage(tenantId);
  const contractAcceptance = useCACContractAcceptance(tenantId);

  const items = useMemo(() => catalogQuery.data ?? [], [catalogQuery.data]);
  const filtered = useMemo(() => {
    if (!search.trim()) {
      return items;
    }
    const q = search.trim().toLowerCase();
    return items.filter((item: CACCatalogItem) => {
      return (
        item.artifact_slug.toLowerCase().includes(q) ||
        item.kind.toLowerCase().includes(q) ||
        (item.title || '').toLowerCase().includes(q)
      );
    });
  }, [items, search]);

  const publicationRequests = publicationRequestsQuery.data ?? [];

  const actorId = currentTenant?.slug || 'tenant-admin';

  const handleAccept = async (item: CACCatalogItem) => {
    if (!item.offer_id) {
      return;
    }
    await acceptOffer.mutateAsync({
      offerId: item.offer_id,
      acceptedById: actorId,
    });
  };

  const handleImport = async (item: CACCatalogItem) => {
    if (!item.latest_version_id) {
      return;
    }
    if (item.license_profile_id && item.offer_id) {
      setPendingImport(item);
      setContractAccepted(false);
      return;
    }
    await importPackage.mutateAsync({
      versionId: item.latest_version_id,
      regionCode,
      acceptedById: actorId,
    });
  };

  const handleContractAccepted = async () => {
    if (!pendingImport || !pendingImport.latest_version_id || !tenantId) {
      return;
    }
    const item = pendingImport;
    const versionId: string = pendingImport.latest_version_id as string;
    await contractAcceptance.mutateAsync({
      offer_id: item.offer_id as string,
      tenant_id: tenantId,
      accepted_by_id: actorId,
      license_profile_id: item.license_profile_id as string,
    });
    await importPackage.mutateAsync({
      versionId,
      regionCode,
      acceptedById: actorId,
    });
    setPendingImport(null);
    setContractAccepted(false);
  };

  const handleContractCancel = () => {
    setPendingImport(null);
    setContractAccepted(false);
  };

  return (
    <div className="space-y-6" data-testid="cac-marketplace-page">
      <PageHeader
        icon={PackageSearch}
        title="CAC Marketplace"
        subtitle="Discover catalog artifacts, accept eligible offers, and request package imports."
        testId="marketplace-header"
      />

      {!tenantId ? (
        <Card className="p-6 border-amber-200 dark:border-amber-800">
          <div className="flex items-center gap-2 text-amber-700 dark:text-amber-300">
            <ShieldAlert className="h-4 w-4" />
            Select a tenant to access CAC marketplace operations.
          </div>
        </Card>
      ) : null}

      <Card className="p-4 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <SearchInput
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search artifact slug, kind or offer title"
            className="md:col-span-2"
          />
          <Input
            value={regionCode}
            onChange={(e) => setRegionCode(e.target.value)}
            placeholder="Target DR region code"
          />
        </div>
        <p className="text-xs text-muted-foreground" data-testid="cac-private-offer-note">
          Private offers are filtered by backend eligibility and never expose target metadata in UI.
        </p>
      </Card>

      {catalogQuery.isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Spinner />
        </div>
      ) : catalogQuery.isError ? (
        <Card className="p-6 border-destructive/30 text-destructive">
          {publicErrorMessage(catalogQuery.error)}
        </Card>
      ) : filtered.length === 0 ? (
        <Card className="p-8 text-center text-muted-foreground">
          No discoverable artifacts for the current filters.
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((item: CACCatalogItem) => (
            <MarketplaceCard
              key={item.artifact_id}
              artifactId={item.artifact_id}
              slug={item.artifact_slug}
              kind={item.kind}
              visibility={item.visibility}
              offerType={item.offer_type}
              offerTitle={item.title}
              description={item.description ?? item.summary ?? undefined}
              onAcceptOffer={item.offer_id && tenantId ? () => void handleAccept(item) : undefined}
              onImport={item.latest_version_id && tenantId ? () => void handleImport(item) : undefined}
            />
          ))}
        </div>
      )}

      {acceptOffer.isError ? (
        <Card className="p-4 border-destructive/30 text-destructive" data-testid="cac-accept-offer-error">
          {publicErrorMessage(acceptOffer.error)}
        </Card>
      ) : null}

      {importPackage.isError ? (
        <Card className="p-4 border-destructive/30 text-destructive" data-testid="cac-import-package-error">
          {publicErrorMessage(importPackage.error)}
        </Card>
      ) : null}

      {publicationRequestsQuery.isError ? (
        <Card className="p-4 border-destructive/30 text-destructive" data-testid="cac-publication-requests-error">
          {publicErrorMessage(publicationRequestsQuery.error)}
        </Card>
      ) : null}

      {publicationRequests.length > 0 ? (
        <Card className="p-4" data-testid="cac-publication-requests-panel">
          <h3 className="font-semibold mb-2">Publication requests</h3>
          <p className="text-sm text-muted-foreground mb-3">
            Real publication request states for the selected tenant.
          </p>
          <div className="space-y-2">
            {publicationRequests.map((request: CACPublicationRequest) => (
              <div key={request.id} className="text-sm flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="truncate font-mono text-xs">{request.id}</p>
                  <p className="truncate text-muted-foreground">artifact {request.artifact_id}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline">{request.requested_level}</Badge>
                  {publicationStatusBadge(request)}
                </div>
              </div>
            ))}
          </div>
        </Card>
      ) : null}

      {pendingImport ? (
        <AppModal onClose={handleContractCancel} testId="cac-contract-modal">
          <Card className="w-full max-w-lg p-6 space-y-4 m-4">
            <h2 className="text-lg font-semibold">License Terms — {pendingImport.artifact_slug}</h2>
            {pendingImport.title ? (
              <Badge variant="secondary" className="text-xs">{pendingImport.title}</Badge>
            ) : null}
            <p className="text-sm text-muted-foreground">
              This artifact is licensed. You must accept the license terms before importing it
              into your tenant.
            </p>
            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={contractAccepted}
                onChange={(e) => setContractAccepted(e.target.checked)}
                className="mt-0.5"
              />
              <span>I have read and accept the license terms for this artifact.</span>
            </label>
            {contractAcceptance.isError ? (
              <p className="text-sm text-destructive">
                {publicErrorMessage(contractAcceptance.error)}
              </p>
            ) : null}
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={handleContractCancel}>
                Cancel
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={handleContractAccepted}
                disabled={!contractAccepted || contractAcceptance.isPending || importPackage.isPending}
              >
                {contractAcceptance.isPending || importPackage.isPending ? 'Processing...' : 'Accept and Import'}
              </Button>
            </div>
          </Card>
        </AppModal>
      ) : null}
    </div>
  );
}
